/**
 * Fechamento: contagem por denominação, e a diferença aparecendo enquanto se
 * digita.
 *
 * # Por que denominação, e não um total
 *
 * A spec 05 pede as 12 denominações. Guardar só o total bastaria para a
 * diferença, mas não para a conversa que vem depois dela: com as denominações,
 * "faltam R$ 40" costuma virar "faltam duas notas de 20", que é uma pista, e
 * não um mistério.
 *
 * # Por que a diferença aparece antes de enviar
 *
 * Porque a observação é obrigatória quando há quebra, e descobrir isso no 422
 * significa contar de novo com o diálogo já fechado na cabeça da pessoa. Aqui a
 * conta acompanha a digitação e o campo de observação aparece na hora em que
 * passa a ser exigido.
 *
 * A conta é aritmética INTEIRA em centavos (CONSTITUICAO.md §1) e serve para
 * MOSTRAR. Quem grava o contado é o servidor, a partir das mesmas linhas.
 */
import { useMemo, useState, type FormEvent } from 'react'

import { Botao } from '../../../componentes/Botao'
import { MensagemDeErro } from '../../../componentes/Campos'
import { Dialogo } from '../../../componentes/Dialogo'
import { formatarMoeda, type Centavos } from '../../../plataforma/dinheiro'
import { DENOMINACOES, fecharTurno, somarContagem } from '../../../plataforma/caixa'
import { ErroApi } from '../../../plataforma/api'

export function DialogoFechamento({
  codigo,
  esperado,
  aoFechar,
  aoConcluir,
}: {
  codigo: string
  esperado: Centavos
  aoFechar: () => void
  aoConcluir: () => void
}) {
  const [quantidades, setQuantidades] = useState<Record<number, number>>({})
  const [observacao, setObservacao] = useState('')
  const [erro, setErro] = useState('')
  const [enviando, setEnviando] = useState(false)

  const contado = useMemo(() => somarContagem(quantidades), [quantidades])
  const diferenca = contado - esperado
  const precisaExplicar = diferenca !== 0

  function definir(denominacao: number, bruto: string) {
    const quantidade = bruto === '' ? 0 : Number.parseInt(bruto, 10)
    if (Number.isNaN(quantidade) || quantidade < 0) return
    setQuantidades((atual) => ({ ...atual, [denominacao]: quantidade }))
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (enviando) return

    if (precisaExplicar && observacao.trim() === '') {
      setErro('Explique a diferença antes de fechar.')
      return
    }

    setEnviando(true)
    setErro('')
    try {
      await fecharTurno(codigo, {
        // Sempre as 12 linhas: gaveta vazia é quantidade zero em cada
        // denominação, e não contagem em branco — que o servidor recusa, com
        // razão, porque "esqueci de contar" e "não tinha nada" gravariam igual.
        contagem: DENOMINACOES.map((denominacao) => ({
          denominacao_centavos: denominacao,
          quantidade: quantidades[denominacao] ?? 0,
        })),
        observacao: observacao.trim() || undefined,
      })
      aoConcluir()
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha.message : 'Não foi possível fechar o caixa.')
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo="Fechar caixa"
      icone="lock"
      descricao="Conte a gaveta e informe quantas notas e moedas de cada valor. Depois de fechado, o turno não muda mais."
      aoFechar={aoFechar}
    >
      <form onSubmit={enviar} className="flex flex-col gap-4" aria-label="Fechar caixa">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3">
          {DENOMINACOES.map((denominacao) => (
            <label key={denominacao} className="flex items-center gap-2">
              <span className="tabular w-[62px] shrink-0 text-right text-[13px] text-texto-suave">
                {formatarMoeda(denominacao)}
              </span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className="campo tabular h-9 w-full px-2.5 text-right text-[13.5px]"
                value={quantidades[denominacao] ?? ''}
                onChange={(e) => definir(denominacao, e.target.value)}
                aria-label={`Quantidade de ${formatarMoeda(denominacao)}`}
              />
            </label>
          ))}
        </div>

        <div className="rounded-sistema border border-borda bg-fundo px-4 py-3">
          <Linha rotulo="Contado" valor={contado} />
          <Linha rotulo="Esperado" valor={esperado} />

          <div className="mt-2 flex items-baseline justify-between border-t border-divisoria pt-2">
            <span className="text-[13px] font-semibold text-texto">
              {diferenca === 0 ? 'Sem diferença' : diferenca < 0 ? 'Quebra' : 'Sobra'}
            </span>
            <span
              className={`tabular font-titulo text-[19px] font-bold ${
                diferenca === 0 ? 'text-ok' : diferenca < 0 ? 'text-erro' : 'text-atencao'
              }`}
            >
              {formatarMoeda(Math.abs(diferenca))}
            </span>
          </div>
        </div>

        {/* Aparece exatamente quando passa a ser exigido. */}
        {precisaExplicar && (
          <div>
            <label
              htmlFor="observacao-fechamento"
              className="mb-2 block text-[13px] font-semibold text-texto"
            >
              O que explica a diferença?
            </label>
            <textarea
              id="observacao-fechamento"
              rows={3}
              className="campo w-full px-3 py-2 text-[13.5px]"
              placeholder="Ex.: troco a mais devolvido na venda V0042"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              aria-invalid={observacao.trim() === '' || undefined}
            />
            <p className="mt-1.5 text-[12px] text-texto-fraco">
              É o que transforma uma quebra num fato investigável — sem ela, o relatório vira uma
              lista de números sem causa.
            </p>
          </div>
        )}

        {erro && <MensagemDeErro>{erro}</MensagemDeErro>}

        <div className="mt-1 flex justify-end gap-2.5">
          <Botao type="button" variante="secundario" porte="medio" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao type="submit" porte="medio" disabled={enviando}>
            {enviando ? 'Fechando…' : 'Fechar caixa'}
          </Botao>
        </div>
      </form>
    </Dialogo>
  )
}

function Linha({ rotulo, valor }: { rotulo: string; valor: Centavos }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-[13px] text-texto-suave">{rotulo}</span>
      <span className="tabular text-[13.5px] font-semibold text-tinta">{formatarMoeda(valor)}</span>
    </div>
  )
}
